import cn from 'classnames';
import { MdOutlineFilterAlt } from 'react-icons/md';
import { Checkbox, Listbox, ListboxItem, Popover, PopoverContent, PopoverTrigger, Selection } from '@ds';
import { NameByValue } from '@/types';

type DayHeaderFilterDropdownProps<S extends string> = {
  title: string;
  optionList: NameByValue<S>[];
  selectedKeys?: Set<S>;
  onSelectedKeysChange?: (selectedKeys: Set<S>) => void;
  active?: boolean;
};

export const DayColumnHeaderFilterDropdown = <S extends string>({
  title,
  optionList,
  selectedKeys = new Set(),
  onSelectedKeysChange,
  active,
}: DayHeaderFilterDropdownProps<S>) => {
  const onSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      onSelectedKeysChange?.(new Set(optionList.map((o) => o.value)));
    } else {
      onSelectedKeysChange?.(keys as Set<S>);
    }
  };

  return (
    <Popover
      placement="bottom-end"
      triggerType="listbox"
      triggerScaleOnOpen={false}
      offset={8}
      classNames={{
        content: 'p-1',
      }}
    >
      <PopoverTrigger>
        <button className="flex items-center p-1 rounded-2xl">
          {selectedKeys.size > 0 ? (
            <div
              className={cn(
                'flex items-center justify-center h-4 min-w-4 p-1 text-base-90 rounded-2xl text-xs-sb',
                active ? 'bg-base-30' : 'bg-base-20',
              )}
            >
              {selectedKeys.size}
            </div>
          ) : (
            <MdOutlineFilterAlt
              className={cn(active ? 'text-base-30 hover:text-base-40' : 'text-base-30 hover:text-base-20')}
              size={16}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <Listbox
          aria-label={title}
          selectionMode="multiple"
          selectedKeys={selectedKeys}
          onSelectionChange={onSelectionChange}
          classNames={{
            base: 'p-0',
          }}
        >
          {optionList.map((item) => (
            <ListboxItem
              startContent={
                <Checkbox
                  className="pr-0 pointer-events-none"
                  size="sm"
                  isReadOnly
                  isSelected={selectedKeys.has(item.value)}
                />
              }
              key={item.value}
              textValue={item.name}
              hideSelectedIcon
              classNames={{
                base: 'p-0 text-s-r gap-0',
                title: 'pr-2',
              }}
            >
              {item.name}
            </ListboxItem>
          ))}
        </Listbox>
      </PopoverContent>
    </Popover>
  );
};
